/**
 * Utility helpers for UI Label Translate.
 * This file contains shared pure helpers used by multiple screens, services, or wrappers.
 */
import { TranslateService } from '@ngx-translate/core';

const LABEL_KEY_ALIASES: Record<string, string[]> = {
  'request.access': ['auth.signup.actions.requestAccess'],
  'back.to.sign.in': ['auth.signup.actions.backToSignIn'],
  'dark.theme': ['topbar.theme.dark'],
  'light.theme': ['topbar.theme.light'],
  'show.password': ['shared.form.showPassword'],
  'hide.password': ['shared.form.hidePassword'],
  'drop.files.here.or.click.to.browse': ['shared.form.dropFiles'],
  'field.hint': ['shared.form.fieldHint'],
  retry: ['shared.buttons.retry']
};

export function resolveTranslatedUiLabel(
  translate: TranslateService,
  value: string | null | undefined
): string {
  const label = String(value ?? '');

  if (!label.trim()) {
    return label;
  }

  const directTranslation = translate.instant(label);
  if (typeof directTranslation === 'string' && directTranslation !== label) {
    return directTranslation;
  }

  const normalized = normalizeUiLabel(label);
  const camelCase = dottedToCamelCase(normalized);
  const candidates = [
    ...(LABEL_KEY_ALIASES[normalized] ?? []),
    `uiLabels.${normalized}`,
    `shared.buttons.${normalized}`,
    `common.${camelCase}`
  ];

  for (const key of candidates) {
    const translated = translate.instant(key);
    if (typeof translated === 'string' && translated !== key) {
      return translated;
    }
  }

  return label;
}

export function normalizeUiLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\+/g, ' plus ')
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '');
}

function dottedToCamelCase(value: string): string {
  return value.replace(/\.([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}
